import React, { useContext } from "react";
import { useState, useEffect } from "react";
import RegistrationContext from "../../contexts/RegistrationContext";
import { base_url, stripe_key } from "../../config";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(stripe_key || "");


type eventOptionsIds =
  | "reception"
  | "studentReception"
  | "banquetTicket"
  | "diversityAndInclusion";
type eventOptionsType = {
  id: eventOptionsIds;
  name: string;
  price: number;
};

const allDays = [
  // Workshop days
  {
    id: "ws-aug15-bremen",
    type: "workshop",
    date: "August 15th",
    location: "Bremen",
  },
  {
    id: "ws-aug16-bremen",
    type: "workshop",
    date: "August 16th",
    location: "Bremen",
  },
  {
    id: "ws-aug17-bremen",
    type: "workshop",
    date: "August 17th",
    location: "Bremen",
  },
  // Tutorial days
  {
    id: "tut-aug15-bremen",
    type: "tutorial",
    date: "August 15th",
    location: "Bremen",
  },
  {
    id: "tut-aug16-bremen",
    type: "tutorial",
    date: "August 16th",
    location: "Bremen",
  },
  {
    id: "tut-aug17-bremen",
    type: "tutorial",
    date: "August 17th",
    location: "Bremen",
  },
];

const eventOptions: eventOptionsType[] = [
  {
    id: "reception",
    name: "Reception",
    price: 100,
  },
  {
    id: "studentReception",
    name: "Student Reception",
    price: 90,
  },
  {
    id: "banquetTicket",
    name: "Banquet Ticket",
    price: 135,
  },
  {
    id: "diversityAndInclusion",
    name: "D&I Lunch on August 19th",
    price: 12,
  },
];

type UserData = {
  workshops: string[];
  tutorials: string[];
  events: { [key in eventOptionsIds]: number };
  invitationLetter: boolean;
  hasMainVisaLetter: boolean;
};

export default function WorkshopTutorialDayManagerWrapper() {
  const { data } = useContext(RegistrationContext);
  const hasMainVisaLetter = (data.visa_letters || []).some(
    (v: any) => v.type === "main"
  );
  // Mock data - replace with your actual data structure
  const myUserData: UserData = {
    workshops: [],
    tutorials: [],
    events: {
      studentReception: data.cart.extra_student,
      banquetTicket: data.cart.extra_banquet,
      diversityAndInclusion: data.cart.diversity_inclusion,
      reception: data.cart.extra_reception,
    },
    invitationLetter: hasMainVisaLetter,
    hasMainVisaLetter,
  };
  data.workshop_day1 === 1 && myUserData.workshops.push("ws-aug15-bremen");
  data.workshop_day2 === 1 && myUserData.workshops.push("ws-aug16-bremen");
  data.workshop_day3 === 1 && myUserData.workshops.push("ws-aug17-bremen");
  data.tutorial_day1 === 1 && myUserData.tutorials.push("tut-aug15-bremen");
  data.tutorial_day2 === 1 && myUserData.tutorials.push("tut-aug16-bremen");
  data.tutorial_day3 === 1 && myUserData.tutorials.push("tut-aug17-bremen");

  return <WorkshopTutorialDayManager userData={myUserData} />;
}
function WorkshopTutorialDayManager({ userData }: { userData: UserData }) {
  const { setData, setError } = useContext(RegistrationContext);
  const [selectedWorkshops, setSelectedWorkshops] = useState<string[]>(
    userData.workshops
  );

  const [selectedTutorials, setSelectedTutorials] = useState<string[]>(
    userData.tutorials
  );
  const [selectedEvents, setSelectedEvents] = useState(userData.events);
  const [invitationLetter, setInvitationLetter] = useState(
    userData.invitationLetter
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeApplied, setChangeApplied] = useState(false);
  const [priceInfo, setPriceInfo] = useState<{
    cost: number;
    loading: boolean;
  }>({ cost: 0, loading: false });

  const allSelectedIds = [...selectedWorkshops, ...selectedTutorials];
  const currentSelectedIds = [...userData.workshops, ...userData.tutorials];

  const hasChanges =
    JSON.stringify(allSelectedIds.sort()) !==
      JSON.stringify(currentSelectedIds.sort()) ||
    JSON.stringify(selectedEvents) !== JSON.stringify(userData.events) ||
    invitationLetter !== userData.invitationLetter;

  useEffect(() => {
    const fetchPriceFromBackend = async () => {
      try {
        const response = await axios.post(
          `${base_url}/api/registration_upgrade/get_price`,
          {
            workshop_day1: selectedWorkshops.includes("ws-aug15-bremen")
              ? 1
              : 0,
            workshop_day2: selectedWorkshops.includes("ws-aug16-bremen")
              ? 1
              : 0,
            workshop_day3: selectedWorkshops.includes("ws-aug17-bremen")
              ? 1
              : 0,
            tutorial_day1: selectedTutorials.includes("tut-aug15-bremen")
              ? 1
              : 0,
            tutorial_day2: selectedTutorials.includes("tut-aug16-bremen")
              ? 1
              : 0,
            tutorial_day3: selectedTutorials.includes("tut-aug17-bremen")
              ? 1
              : 0,
            extra_reception: selectedEvents.reception || 0,
            extra_student: selectedEvents.studentReception || 0,
            extra_banquet: selectedEvents.banquetTicket || 0,
            diversity_inclusion: selectedEvents.diversityAndInclusion || 0,
            invitation_letter: invitationLetter ? 1 : 0,
          }
        );
        const cost = response.data.price;
        return cost;
      } catch (error) {
        console.error("Error fetching price from backend:", error);
        return 0; // Return 0 in case of error
      }
    };
    if (hasChanges) {
      setPriceInfo({ cost: 0, loading: true });
      fetchPriceFromBackend().then((cost) => {
        setPriceInfo({ cost, loading: false });
      });
    }
  }, [
    selectedWorkshops,
    selectedTutorials,
    selectedEvents,
    invitationLetter,
    hasChanges,
  ]);

  const toggleSelection = (dayId: string, type: string) => {
    if (type === "workshop") {
      setSelectedWorkshops((prev) =>
        prev.includes(dayId)
          ? prev.filter((id) => id !== dayId)
          : [...prev, dayId]
      );
    } else {
      setSelectedTutorials((prev) =>
        prev.includes(dayId)
          ? prev.filter((id) => id !== dayId)
          : [...prev, dayId]
      );
    }
  };

  const updateEventQuantity = (eventId: eventOptionsIds, change: number) => {
    setSelectedEvents((prev) => {
      const newQuantity = Math.max(
        0,
        Math.min(3, (prev[eventId] || 0) + change)
      );
      return { ...prev, [eventId]: newQuantity };
    });
  };

  const handleApplyChanges = async () => {
    setIsSubmitting(true);
    const updatedData = {
      workshop_day1: selectedWorkshops.includes("ws-aug15-bremen") ? 1 : 0,
      workshop_day2: selectedWorkshops.includes("ws-aug16-bremen") ? 1 : 0,
      workshop_day3: selectedWorkshops.includes("ws-aug17-bremen") ? 1 : 0,
      tutorial_day1: selectedTutorials.includes("tut-aug15-bremen") ? 1 : 0,
      tutorial_day2: selectedTutorials.includes("tut-aug16-bremen") ? 1 : 0,
      tutorial_day3: selectedTutorials.includes("tut-aug17-bremen") ? 1 : 0,
      extra_reception: selectedEvents.reception || 0,
      extra_student: selectedEvents.studentReception || 0,
      extra_banquet: selectedEvents.banquetTicket || 0,
      diversity_inclusion: selectedEvents.diversityAndInclusion || 0,
      invitation_letter: invitationLetter ? 1 : 0,
    };
    let reloadData = false;
    let updateApplied = false;
    try {
      const response = await axios.post(
        `${base_url}/api/registration_upgrade/confirm`,
        updatedData
      );

      if (response.data.type === "stripe") {
        const stripe = await stripePromise;
        if (stripe) {
          await stripe.redirectToCheckout({
            sessionId: response.data.payload.id,
          });
        } else {
          console.error("Stripe failed to load.");
          setError?.("Stripe failed to load. Please try again.");
        }
      }
      if (response.data.type === "update") {
        reloadData = true;
        updateApplied = true;
      }
      if (response.data.type === "refund") {
        reloadData = true;
        updateApplied = true;
      }
    } catch (error: any) {
      console.error("Error applying registration update:", error);
      setError?.(
        "Could not apply changes. " +
          (error?.response?.data?.message || error?.message || "Unknown error")
      );
    }

    if (reloadData) {
      try {
        const response = await axios.get(`${base_url}/api/progress`);
        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching updated data:", error);
      }
    }

    setIsSubmitting(false);
    if (updateApplied) {
      setChangeApplied(true);
      setShowConfirmation(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getChangeStatus = (dayId: string, type: string) => {
    const currentlySelected =
      type === "workshop"
        ? userData.workshops.includes(dayId)
        : userData.tutorials.includes(dayId);
    const newlySelected =
      type === "workshop"
        ? selectedWorkshops.includes(dayId)
        : selectedTutorials.includes(dayId);
    if (currentlySelected && !newlySelected) return "removing";
    if (!currentlySelected && newlySelected) return "adding";
    return "no-change";
  };

  const getEventChangeStatus = (eventId: eventOptionsIds) => {
    const current = userData.events[eventId] || 0;
    const selected = selectedEvents[eventId] || 0;
    if (current < selected) return "increasing";
    if (current > selected) return "decreasing";
    return "no-change";
  };

  if (changeApplied) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white border rounded-lg p-6 text-center">
          <div className="h-12 w-12 text-green-500 mx-auto mb-3">
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              className="w-full h-full"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-green-700 mb-2">
            Selections updated!
          </h2>
          <p className="text-gray-600 mb-3">
            Changes applied to your registration.
          </p>
          <button
            onClick={() => setChangeApplied(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium"
          >
            Make More Changes
          </button>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    const workshopsToAdd = selectedWorkshops.filter(
      (id) => !userData.workshops.includes(id)
    );
    const workshopsToRemove = userData.workshops.filter(
      (id) => !selectedWorkshops.includes(id)
    );
    const tutorialsToAdd = selectedTutorials.filter(
      (id) => !userData.tutorials.includes(id)
    );
    const tutorialsToRemove = userData.tutorials.filter(
      (id) => !selectedTutorials.includes(id)
    );

    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Confirm changes</h2>
          </div>
          <div className="p-4 space-y-4">
            {(workshopsToAdd.length > 0 || tutorialsToAdd.length > 0) && (
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">
                  Adding:
                </p>
                <ul className="text-sm space-y-1">
                  {workshopsToAdd.map((id) => {
                    const day = allDays.find((d) => d.id === id);
                    return (
                      <li key={id}>
                        • Workshop {day?.date} ({day?.location})
                      </li>
                    );
                  })}
                  {tutorialsToAdd.map((id) => {
                    const day = allDays.find((d) => d.id === id);
                    return (
                      <li key={id}>
                        • Tutorial {day?.date} ({day?.location})
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {(workshopsToRemove.length > 0 || tutorialsToRemove.length > 0) && (
              <div>
                <p className="text-sm font-medium text-red-600 mb-1">
                  Removing:
                </p>
                <ul className="text-sm space-y-1">
                  {workshopsToRemove.map((id) => {
                    const day = allDays.find((d) => d.id === id);
                    return (
                      <li key={id}>
                        • Workshop {day?.date} ({day?.location})
                      </li>
                    );
                  })}
                  {tutorialsToRemove.map((id) => {
                    const day = allDays.find((d) => d.id === id);
                    return (
                      <li key={id}>
                        • Tutorial {day?.date} ({day?.location})
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {invitationLetter !== userData.invitationLetter && (
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">
                  Other changes:
                </p>
                <ul className="text-sm space-y-1">
                  <li>
                    • Visa letter request:{" "}
                    {userData.invitationLetter ? "yes" : "no"} →{" "}
                    {invitationLetter ? "yes" : "no"}
                  </li>
                </ul>
              </div>
            )}

            {/* Event Changes */}
            {eventOptions.some(
              (event) => getEventChangeStatus(event.id) !== "no-change"
            ) && (
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">
                    Event changes:
                  </p>
                  <ul className="text-sm space-y-1">
                    {eventOptions.map((event) => {
                      const status = getEventChangeStatus(event.id);
                      const current = userData.events[event.id] || 0;
                      const selected = selectedEvents[event.id] || 0;
                      if (status === "no-change") return null;
                      return (
                        <li key={event.id}>
                          • {event.name}: {current} → {selected}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

            <div className="bg-gray-50 p-3 rounded">
              <div className="flex justify-between items-center">
                <span className="font-medium">Cost change:</span>
                <span
                  className={`font-bold ${priceInfo.cost >= 0 ? "text-red-600" : "text-green-600"
                    }`}
                >
                  {priceInfo.cost >= 0 ? "+" : ""}
                  {formatCurrency(priceInfo.cost)}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyChanges}
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded font-medium"
              >
                {isSubmitting ? "Updating..." : "Confirm"}
              </button>
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Current Selections */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h2 className="font-semibold text-blue-800 mb-2">Current Selections</h2>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-700">Workshops:</span>
            {userData.workshops.length > 0 ? (
              <ul className="mt-1">
                {userData.workshops.map((id) => {
                  const day = allDays.find((d) => d.id === id);
                  return (
                    <li key={id}>
                      • {day?.date} ({day?.location})
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-gray-600 italic mt-1">None selected</p>
            )}
          </div>
          <div>
            <span className="font-medium text-blue-700">Tutorials:</span>
            {userData.tutorials.length > 0 ? (
              <ul className="mt-1">
                {userData.tutorials.map((id) => {
                  const day = allDays.find((d) => d.id === id);
                  return (
                    <li key={id}>
                      • {day?.date} ({day?.location})
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-gray-600 italic mt-1">None selected</p>
            )}
          </div>
          <div>
            <span className="font-medium text-blue-700">
              Purchased social events:
            </span>
            <ul className="mt-1">
              {eventOptions.map((event) => {
                const quantity = userData.events[event.id] || 0;
                return (
                  <li key={event.id}>
                    • {event.name}: {quantity}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Update Selections */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Update to the following selection:</h2>
        </div>
        <div className="p-4 space-y-4">
          {/* Workshop and Tutorial Days */}
          <div className="space-y-2">
            {allDays.map((day) => {
              const isSelected =
                day.type === "workshop"
                  ? selectedWorkshops.includes(day.id)
                  : selectedTutorials.includes(day.id);
              const changeStatus = getChangeStatus(day.id, day.type);

              return (
                <div
                  key={day.id}
                  className="flex items-center justify-between pb-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      {day.type === "workshop" ? "Workshop" : "Tutorial"}{" "}
                      {day.date} ({day.location})
                    </span>
                    <div className="flex items-center gap-1 text-sm">
                      <span
                        className={
                          isSelected
                            ? "font-bold text-green-600"
                            : "text-gray-500"
                        }
                      >
                        yes
                      </span>
                      <span>/</span>
                      <span
                        className={
                          !isSelected
                            ? "font-bold text-red-600"
                            : "text-gray-500"
                        }
                      >
                        no
                      </span>
                    </div>
                    {changeStatus === "adding" && (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                        Adding
                      </span>
                    )}
                    {changeStatus === "removing" && (
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                        Removing
                      </span>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(day.id, day.type)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              );
            })}
          </div>

          {/* Visa letter request */}
          {!userData.hasMainVisaLetter && (
            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Visa letter request:</h3>
              <div className="flex items-center justify-between pb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    Request an invitation / visa letter
                  </span>
                  <div className="flex items-center gap-1 text-sm">
                    <span
                      className={
                        invitationLetter
                          ? "font-bold text-green-600"
                          : "text-gray-500"
                      }
                    >
                      yes
                    </span>
                    <span>/</span>
                    <span
                      className={
                        !invitationLetter
                          ? "font-bold text-red-600"
                          : "text-gray-500"
                      }
                    >
                      no
                    </span>
                  </div>
                  {invitationLetter !== userData.invitationLetter &&
                    invitationLetter && (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                        Adding
                      </span>
                    )}
                  {invitationLetter !== userData.invitationLetter &&
                    !invitationLetter && (
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                        Removing
                      </span>
                    )}
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invitationLetter}
                    onChange={() => setInvitationLetter((v) => !v)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Free of charge. After confirming you'll be asked to fill in
                passport and travel details so an invitation letter can be
                issued.
              </p>
            </div>
          )}

          {/* Event Options */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-3">Social Events:</h3>
            <div className="">
              {eventOptions.map((event) => {
                const quantity = selectedEvents[event.id] || 0;
                const changeStatus = getEventChangeStatus(event.id);

                return (
                  <div
                    key={event.id}
                    className="flex items-center justify-between pb-1"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{event.name}</span>
                      <span className="text-xs text-gray-500">
                        ({formatCurrency(event.price)} each)
                      </span>
                      {changeStatus === "increasing" && (
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                          ↑
                        </span>
                      )}
                      {changeStatus === "decreasing" && (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                          ↓
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateEventQuantity(event.id, -1)}
                        disabled={quantity <= 0}
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded flex items-center justify-center text-lg font-bold"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-medium">
                        {quantity}
                      </span>
                      <button
                        onClick={() => updateEventQuantity(event.id, 1)}
                        disabled={quantity >= 3}
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded flex items-center justify-center text-lg font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Cost Summary */}
      {hasChanges && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <h3 className="font-medium mb-2">
            {priceInfo.cost >= 0 ? (
              "Cost of Changes"
            ) : (
              <>
                Refundable Amount
                <span
                  className="ml-1 cursor-pointer text-gray-400"
                  title="A cancellation fee is included in the amount shown for all partial cancellations."
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    <line
                      x1="12"
                      y1="8"
                      x2="12"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle cx="12" cy="16" r="1" fill="currentColor" />
                  </svg>
                </span>
              </>
            )}
          </h3>
          {priceInfo.loading ? (
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="h-4 w-4 animate-spin">
                <svg className="w-full h-full" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <span className="text-gray-600 text-sm">Calculating...</span>
            </div>
          ) : (
            <div className="text-xl font-bold mb-2">
              <span className={"text-blue-600"}>
                {priceInfo.cost >= 0
                  ? formatCurrency(priceInfo.cost)
                  : formatCurrency(-priceInfo.cost)}
              </span>
            </div>
          )}

          <button
            onClick={() => setShowConfirmation(true)}
            disabled={priceInfo.loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded font-medium"
          >
            {priceInfo.loading ? "Please wait..." : "Proceed"}
          </button>
        </div>
      )}
    </div>
  );
}
